import * as React from 'react';
import { Project } from '@shared/types/tsk';
import { Info } from 'lucide-react';

interface MetadataItemProps {
  label: string;
  value: string | undefined;
  full?: boolean;
}

export const MetadataItem = ({ label, value, full }: MetadataItemProps) => (
  <div className={full ? 'col-span-2' : ''}>
    <label className="text-[10px] font-black uppercase tracking-[0.2em] text-[#94a3b8] block mb-2">{label}</label>
    <p className="text-[18px] font-black text-[#0f172a] border-b-2 border-[#f1f5f9] pb-3 leading-tight truncate">
      {value || 'DATA NOT ENTERED'}
    </p>
  </div>
);

interface GeminiPage1ContentProps {
  project: Project | undefined;
  fixtureCounts: Record<string, number>;
  fittingCounts: Record<string, number>;
  aiStatement: string;
}

export const GeminiPage1Content = ({ project, fixtureCounts, fittingCounts, aiStatement }: GeminiPage1ContentProps) => {
  return (
    <>
      <div className="text-center space-y-3 border-b-2 border-slate-900 pb-10">
        <h1 className="text-3xl font-black uppercase tracking-tighter text-[#0f172a] leading-tight">
          As Constructed Report
        </h1>
        <p className="text-lg font-bold text-[#475569] uppercase tracking-widest">Water and Sanitary Drainage</p>
        <div className="flex justify-center items-center gap-6 text-[10px] font-bold text-[#94a3b8] uppercase tracking-[0.2em]">
          <span>Document v1.0</span>
          <span>•</span>
          <span>Ref: TS-{project?.id?.slice(-5) || '00000'}</span>
          <span>•</span>
          <span>{new Date().toLocaleDateString('en-AU')}</span>
        </div>
      </div>
      <section className="mt-12 bg-[#f8fafc] p-10 border border-[#e2e8f0] rounded-2xl space-y-10">
        <div className="grid grid-cols-2 gap-y-8 gap-x-12">
          <MetadataItem label="Property Address" value={project?.address} full />
          <MetadataItem label="Site Classification" value={project?.siteClassification} />
          <MetadataItem label="Approval No." value={project?.approvalNo} />
          <MetadataItem label="Lot" value={project?.lot} />
          <MetadataItem label="RP / Plan No." value={project?.rp} />
        </div>
      </section>
      <div className="mt-12 grid grid-cols-2 gap-12">
        <section className="space-y-5">
          <h2 className="text-[11px] font-black uppercase tracking-widest text-[#64748b]">Fixture Schedule</h2>
          <div className="border-2 border-[#0f172a] rounded-xl overflow-hidden shadow-sm">
            <table className="w-full border-collapse zebra-table">
              <thead className="bg-[#0f172a] text-white">
                <tr>
                  <th className="text-left px-5 py-4 text-[10px] uppercase font-black tracking-wider">Fixture</th>
                  <th className="text-right px-5 py-4 text-[10px] uppercase font-black tracking-wider w-20">Qty</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#f1f5f9]">
                {Object.entries(fixtureCounts).length > 0 ? (
                  Object.entries(fixtureCounts).map(([name, count]) => (
                    <tr key={name} className="hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-3.5 text-[14px] font-bold text-[#334155]">{name}</td>
                      <td className="px-5 py-3.5 text-[14px] font-black text-right text-[#0f172a]">{count}</td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={2} className="px-5 py-10 text-[12px] italic text-slate-400 text-center">No fixtures mapped</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
        <section className="space-y-5">
          <h2 className="text-[11px] font-black uppercase tracking-widest text-[#64748b]">Fittings & Details</h2>
          <div className="border-2 border-[#0f172a] rounded-xl overflow-hidden shadow-sm">
            <table className="w-full border-collapse zebra-table">
              <thead className="bg-[#0f172a] text-white">
                <tr>
                  <th className="text-left px-5 py-4 text-[10px] uppercase font-black tracking-wider">Item</th>
                  <th className="text-right px-5 py-4 text-[10px] uppercase font-black tracking-wider w-20">Qty</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#f1f5f9]">
                {Object.entries(fittingCounts).length > 0 ? (
                  Object.entries(fittingCounts).map(([name, count]) => (
                    <tr key={name} className="hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-3.5 text-[14px] font-bold text-[#334155]">{name}</td>
                      <td className="px-5 py-3.5 text-[14px] font-black text-right text-[#0f172a]">{count}</td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={2} className="px-5 py-10 text-[12px] italic text-slate-400 text-center">No fittings mapped</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
      {aiStatement && (
        <section className="mt-12 pt-10 border-t-2 border-[#f1f5f9] flex gap-6">
          <div className="p-3 bg-blue-50 text-blue-600 rounded-2xl shrink-0 h-fit">
            <Info size={24} />
          </div>
          <p className="text-base text-[#475569] font-medium italic leading-relaxed">
            {aiStatement}
          </p>
        </section>
      )}
      <div className="mt-auto flex justify-between items-end pt-12 border-t-4 border-brand-orange">
        <div className="space-y-5">
          <div className="w-80 h-28 border-2 border-dashed border-brand-orange/20 flex items-center justify-center text-xs font-black uppercase text-slate-200 tracking-[0.4em] rounded-xl">Signature Area</div>
          <div className="w-80 border-b-2 border-[#0f172a]" />
          <p className="text-[10px] font-black uppercase tracking-widest text-brand-orange">Authorised Professional Verification</p>
        </div>
        <div className="text-right">
          <p className="text-[10px] font-bold text-[#94a3b8] uppercase tracking-widest">Page 01 / 02</p>
          <p className="text-[10px] font-black text-[#0f172a] uppercase mt-2">TradeSketch Output</p>
        </div>
      </div>
    </>
  );
};
